<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
    show: Boolean,
    total: Number
});

const emit = defineEmits(['close', 'confirm', 'cancel']);

const amountReceived = ref('');
const amountError = ref('');

const change = computed(() => {
    const received = parseFloat(amountReceived.value) || 0;
    const total = props.total || 0;
    const rawChange = received - total;
    // Redondear al múltiplo de 0.50 más cercano
    return Math.max(0, Math.round(rawChange * 2) / 2);
});

const isValid = computed(() => {
    const received = parseFloat(amountReceived.value) || 0;
    return received >= props.total;
});

watch(() => props.show, (newVal) => {
    if (newVal) {
        amountReceived.value = '';
        amountError.value = '';
    }
});

function handleConfirm() {
    const received = parseFloat(amountReceived.value) || 0;

    if (received < props.total) {
        amountError.value = 'El monto recibido debe ser mayor o igual al total';
        return;
    }

    amountError.value = '';
    emit('confirm', {
        amountReceived: received,
        change: change.value
    });
}

function handleCancel() {
    emit('cancel');
}

// Formatear input como moneda mientras escribe
function formatCurrency(event) {
    const value = event.target.value.replace(/[^0-9.]/g, '');
    // Permitir solo un punto decimal
    const parts = value.split('.');
    if (parts.length > 2) {
        event.target.value = parts[0] + '.' + parts.slice(1).join('');
    }
    amountError.value = '';
}
</script>

<template>
    <Transition name="modal">
        <div v-if="show" class="modal-overlay" @click.self="handleCancel">
            <div class="modal-container">
                <!-- Header -->
                <header class="modal-header">
                    <div class="header-icon">
                        <span class="material-symbols-rounded">payments</span>
                    </div>
                    <div>
                        <h2 class="modal-title">Pago en efectivo</h2>
                        <p class="modal-subtitle">Ingresa el monto recibido</p>
                    </div>
                    <button class="close-btn" @click="handleCancel" aria-label="Cerrar">
                        <span class="material-symbols-rounded">close</span>
                    </button>
                </header>

                <!-- Body -->
                <div class="modal-body">
                    <!-- Total Amount -->
                    <div class="total-section">
                        <span class="total-label">Total a cobrar</span>
                        <span class="total-value">${{ total?.toFixed(2) || '0.00' }}</span>
                    </div>

                    <!-- Amount Received Input -->
                    <div class="input-section">
                        <label class="input-label">
                            <span class="material-symbols-rounded icon">wallet</span>
                            Monto recibido
                        </label>
                        <div class="input-wrapper">
                            <span class="currency-symbol">$</span>
                            <input v-model="amountReceived" type="number" step="0.01" class="input-field"
                                :class="{ 'input-error': amountError }" placeholder="0.00" @input="formatCurrency"
                                autofocus />
                        </div>
                        <p v-if="amountError" class="error-message">
                            <span class="material-symbols-rounded">error</span>
                            {{ amountError }}
                        </p>
                    </div>

                    <!-- Change Display -->
                    <div class="change-section" :class="{ 'has-change': change > 0 }">
                        <div class="change-content">
                            <div class="change-icon">
                                <span class="material-symbols-rounded">monetization_on</span>
                            </div>
                            <div class="change-info">
                                <span class="change-label">Cambio a devolver</span>
                                <span class="change-value">${{ change.toFixed(2) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <footer class="modal-footer">
                    <button class="btn btn-cancel" @click="handleCancel">
                        Cancelar
                    </button>
                    <button class="btn btn-confirm" @click="handleConfirm" :disabled="!isValid">
                        <span class="material-symbols-rounded">check_circle</span>
                        Confirmar pago
                    </button>
                </footer>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 480px;
    overflow: hidden;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    padding: 1.5rem 1.75rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.header-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.header-icon .material-symbols-rounded {
    font-size: 28px;
    color: white;
}

.modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.modal-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0.25rem 0 0 0;
}

.close-btn {
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
}

.close-btn:hover {
    background: #f3f4f6;
    color: #1f2937;
}

.modal-body {
    padding: 1.75rem;
}

.total-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    border-radius: 12px;
    margin-bottom: 1.5rem;
}

.total-label {
    color: #6b7280;
    font-size: 0.9rem;
    font-weight: 500;
}

.total-value {
    color: #1f2937;
    font-size: 1.875rem;
    font-weight: 700;
    letter-spacing: -0.5px;
}

.input-section {
    margin-bottom: 1.5rem;
}

.input-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
}

.input-label .icon {
    font-size: 20px;
    color: #10b981;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.currency-symbol {
    position: absolute;
    left: 1rem;
    color: #6b7280;
    font-size: 1.25rem;
    font-weight: 600;
    pointer-events: none;
}

.input-field {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.25rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 1.125rem;
    font-weight: 600;
    transition: all 0.2s;
    background: white;
    color: #1f2937;
    font-family: inherit;
    box-sizing: border-box;
}

.input-field:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.input-field::placeholder {
    color: #d1d5db;
    font-weight: 400;
}

.input-field.input-error {
    border-color: #ef4444;
}

.input-field.input-error:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.error-message {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.5rem;
    font-size: 0.8rem;
    color: #ef4444;
    font-weight: 500;
}

.error-message .material-symbols-rounded {
    font-size: 16px;
}

.change-section {
    padding: 1rem;
    background: #f9fafb;
    border: 1.5px dashed #d1d5db;
    border-radius: 12px;
    transition: all 0.3s;
}

.change-section.has-change {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    border-color: #10b981;
    border-style: solid;
}

.change-content {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.change-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s;
}

.has-change .change-icon {
    background: #10b981;
}

.change-icon .material-symbols-rounded {
    font-size: 24px;
    color: #d1d5db;
    transition: all 0.3s;
}

.has-change .change-icon .material-symbols-rounded {
    color: white;
}

.change-info {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.change-label {
    color: #6b7280;
    font-size: 0.9rem;
    font-weight: 500;
}

.has-change .change-label {
    color: #065f46;
}

.change-value {
    color: #9ca3af;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.5px;
    transition: all 0.3s;
}

.has-change .change-value {
    color: #065f46;
}

.modal-footer {
    padding: 1.25rem 1.75rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 0.75rem;
    background: #f9fafb;
}

.btn {
    flex: 1;
    padding: 0.875rem 1.5rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.btn-cancel {
    background: white;
    color: #374151;
    border: 1.5px solid #d1d5db;
}

.btn-cancel:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.btn-confirm {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.btn-confirm:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.5);
}

.btn-confirm:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-confirm .material-symbols-rounded {
    font-size: 20px;
}

/* Transiciones del modal */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
    transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
    transform: scale(0.9);
}

/* Responsivo */
@media (max-width: 640px) {
    .modal-container {
        max-width: 100%;
        border-radius: 16px 16px 0 0;
        align-self: flex-end;
    }

    .total-value {
        font-size: 1.5rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
    }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-header {
        border-color: #334155;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .modal-subtitle {
        color: #94a3b8;
    }

    .close-btn {
        color: #94a3b8;
    }

    .close-btn:hover {
        background: #334155;
        color: #e2e8f0;
    }

    .total-section {
        background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
    }

    .total-label {
        color: #94a3b8;
    }

    .total-value {
        color: #e2e8f0;
    }

    .input-label {
        color: #e2e8f0;
    }

    .currency-symbol {
        color: #94a3b8;
    }

    .input-field {
        background: #0f172a;
        border-color: #334155;
        color: #e2e8f0;
    }

    .input-field:focus {
        border-color: #10b981;
        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
    }

    .input-field::placeholder {
        color: #475569;
    }

    .change-section {
        background: #0f172a;
        border-color: #334155;
    }

    .change-section.has-change {
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
        border-color: #10b981;
    }

    .change-icon {
        background: #1e293b;
    }

    .change-icon .material-symbols-rounded {
        color: #475569;
    }

    .has-change .change-icon {
        background: #10b981;
    }

    .has-change .change-icon .material-symbols-rounded {
        color: white;
    }

    .change-label {
        color: #94a3b8;
    }

    .has-change .change-label {
        color: #6ee7b7;
    }

    .change-value {
        color: #64748b;
    }

    .has-change .change-value {
        color: #6ee7b7;
    }

    .modal-footer {
        background: #0f172a;
        border-color: #334155;
    }

    .btn-cancel {
        background: #1e293b;
        color: #e2e8f0;
        border-color: #334155;
    }

    .btn-cancel:hover {
        background: #334155;
        border-color: #475569;
    }
}

:root[data-theme="dark"] {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-header {
        border-color: #334155;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .modal-subtitle {
        color: #94a3b8;
    }

    .close-btn {
        color: #94a3b8;
    }

    .close-btn:hover {
        background: #334155;
        color: #e2e8f0;
    }

    .total-section {
        background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
    }

    .total-label {
        color: #94a3b8;
    }

    .total-value {
        color: #e2e8f0;
    }

    .input-label {
        color: #e2e8f0;
    }

    .currency-symbol {
        color: #94a3b8;
    }

    .input-field {
        background: #0f172a;
        border-color: #334155;
        color: #e2e8f0;
    }

    .input-field:focus {
        border-color: #10b981;
        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
    }

    .input-field::placeholder {
        color: #475569;
    }

    .change-section {
        background: #0f172a;
        border-color: #334155;
    }

    .change-section.has-change {
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
        border-color: #10b981;
    }

    .change-icon {
        background: #1e293b;
    }

    .change-icon .material-symbols-rounded {
        color: #475569;
    }

    .has-change .change-icon {
        background: #10b981;
    }

    .has-change .change-icon .material-symbols-rounded {
        color: white;
    }

    .change-label {
        color: #94a3b8;
    }

    .has-change .change-label {
        color: #6ee7b7;
    }

    .change-value {
        color: #64748b;
    }

    .has-change .change-value {
        color: #6ee7b7;
    }

    .modal-footer {
        background: #0f172a;
        border-color: #334155;
    }

    .btn-cancel {
        background: #1e293b;
        color: #e2e8f0;
        border-color: #334155;
    }

    .btn-cancel:hover {
        background: #334155;
        border-color: #475569;
    }
}
</style>
